/**
 * Wrapper classes and custom properties for Icon List.
 *
 * Shared by edit.jsx and save.jsx so the editor preview and the saved markup
 * cannot drift — a static-save block that computes its wrapper twice is the
 * classic source of "block contains unexpected content" validation errors.
 */

import { resolvePresetValue } from '../../utils/helpers';

/**
 * @param {Object} attributes Block attributes.
 * @return {string} Wrapper class list.
 */
export function getWrapperClasses(attributes) {
  return [
    'brandy-icon-list',
    `is-layout-${attributes.layout}`,
    /*
     * Emitted only when it is not the default. This is a static-save block, so
     * a class added unconditionally would change the markup of every list ever
     * saved and invalidate all of them; left off for `left`, existing content
     * still saves byte-for-byte what it saved before alignment existed.
     */
    attributes.alignment && attributes.alignment !== 'left'
      ? `is-align-${attributes.alignment}`
      : '',
  ]
    .filter(Boolean)
    .join(' ');
}

/**
 * The icon numbers live on the wrapper as custom properties rather than being
 * pushed into each item's markup: items read them through block context in the
 * editor, but the published page has no context — the cascade is what keeps a
 * hundred items in step with one setting.
 *
 * @param {Object} attributes Block attributes.
 * @return {Object} React style object.
 */
export function getWrapperStyles(attributes) {
  const { iconSize, iconColor, iconGap, gap } = attributes;

  const styles = {
    '--brandy-icon-list-icon-size': `${iconSize}px`,
    '--brandy-icon-list-icon-gap': `${iconGap}px`,
    '--brandy-icon-list-gap': `${gap}px`,
  };

  if (iconColor) {
    styles['--brandy-icon-list-icon-color'] = resolvePresetValue(iconColor);
  }

  return styles;
}
